// Facebook Pixel tracking library
// Based on official Next.js example

declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
  }
}

export const FB_PIXEL_ID = process.env.NEXT_PUBLIC_FB_PIXEL_ID;

// ============ Core Functions ============

/**
 * Track PageView event
 */
export const pageview = () => {
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", "PageView");
  }
};

/**
 * Track a standard Facebook Pixel event
 */
export const event = (name: string, options?: Record<string, unknown>) => {
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", name, options);
  }
};

/**
 * Track a custom Facebook Pixel event
 */
export const customEvent = (name: string, options?: Record<string, unknown>) => {
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("trackCustom", name, options);
  }
};

// ============ Standard Event Helpers ============

/**
 * Track Lead event - for contact form submissions, newsletter signups
 */
export const trackLead = (params?: { content_name?: string }) => {
  event("Lead", params);
};

/**
 * Track ViewContent event - for viewing specific pages/content
 */
export const trackViewContent = (params: {
  content_name: string;
  content_category?: string;
  content_type?: string;
}) => {
  event("ViewContent", params);
};

// ============ Custom Event Helpers ============

/**
 * Track CTA click events - replaces InitiateCheckout for marketing site CTAs
 * This is the main event for tracking when users click buttons to go to the app
 */
export const trackCTAClick = (params: {
  content_name: string;
  content_category?: string;
  cta_location?: string;
  destination?: string;
}) => {
  customEvent("CTAClick", params);
};

/**
 * Track template selection on marketing site
 */
export const trackTemplateSelected = (params: {
  content_name: string;
  content_type?: string;
  content_ids?: string[];
}) => {
  customEvent("TemplateSelected", params);
};

/**
 * Track scroll depth
 */
export const trackScrollDepth = (params: { depth_percent: number; page_path: string }) => {
  customEvent("ScrollDepth", params);
};

/**
 * Track sign in button clicks
 */
export const trackSignInClick = (params?: { source?: string }) => {
  customEvent("SignInClick", params);
};

// ============ Scroll Tracking ============

// Keep track of active listener to avoid duplicates if called multiple times incorrectly
let activeScrollCleanup: (() => void) | null = null;
const trackedDepths = new Set<number>();

/**
 * Reset scroll tracking state (e.g. on route change)
 */
export const resetScrollTracking = () => {
  trackedDepths.clear();
};

/**
 * Initialize scroll depth tracking
 * Automatically tracks when user scrolls to 25%, 50%, 75%, and 100% of the page
 * Returns a cleanup function to remove the event listener
 */
export const initScrollTracking = () => {
  if (typeof window === "undefined") return () => {};

  // Clean up existing listener if any
  if (activeScrollCleanup) {
    activeScrollCleanup();
    activeScrollCleanup = null;
  }

  const depths = [25, 50, 75, 100];

  const checkScrollDepth = () => {
    // Safety check
    if (typeof document === "undefined") return;

    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    // Prevent division by zero
    if (documentHeight === 0) return;

    const scrollPercent = ((scrollTop + windowHeight) / documentHeight) * 100;

    depths.forEach((depth) => {
      if (scrollPercent >= depth && !trackedDepths.has(depth)) {
        trackedDepths.add(depth);
        trackScrollDepth({
          depth_percent: depth,
          page_path: window.location.pathname,
        });
      }
    });
  };

  // Throttle scroll events to avoid excessive tracking
  let scrollTimeout: NodeJS.Timeout | null = null;
  const handleScroll = () => {
    if (scrollTimeout) clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(checkScrollDepth, 100);
  };

  window.addEventListener("scroll", handleScroll, { passive: true });

  // Check immediately in case page loads scrolled
  checkScrollDepth();

  const cleanup = () => {
    window.removeEventListener("scroll", handleScroll);
    if (scrollTimeout) clearTimeout(scrollTimeout);
  };

  activeScrollCleanup = cleanup;
  return cleanup;
};
